import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { omit } from 'lodash';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { ResourceSelectorSidebar } from 'src/components/widget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import {
  QuillTheme,
  RichTextEditor,
  RichTextToolbarOptions,
} from 'src/components/widget/richTextWidget';
import { FONTS_OPTIONS } from 'src/constants';
import { useGetProjectConfig } from 'src/hooks';
import { handleComponentConfigChange } from 'src/utils/changeHandler';

import { CommonFormProps } from '../FormSelector.interface';
import { FormWrapper } from '../FormWrapper';
import { convertSpacesToHTMLFormat } from './Disclaimer.helper';
import { useDisclaimerForm } from './Disclaimer.hook';
import {
  DisclaimerConfigSchema,
  IDisclaimerConfig,
} from './Disclaimer.interface';

export const DisclaimerForm = (props: CommonFormProps) => {
  const { data: projectConfig } = useGetProjectConfig();
  const {
    control,
    handleSubmit: submitWrapper,
    reset,
    formState,
    getValues,
  } = useForm<IDisclaimerConfig>({
    values: omit(
      props.componentDetails.config as IDisclaimerConfig,
      'themeId',
      'productList',
    ),
    resolver: ajvResolver(
      DisclaimerConfigSchema as unknown as JSONSchemaType<IDisclaimerConfig>,
    ),
  });

  const { openBackgroundDrawer, onToggleBackgroundDrawer } =
    useDisclaimerForm();

  const handleOnChange = (key: string | string[]) => (value: unknown) => {
    props.onChange(props.componentDetails.config ?? {}, key, value);
  };

  const options = {
    placeholder: 'Enter text...',
    richTextTheme: QuillTheme.TOOLBAR,
  };

  const disabled = false;
  const projectFonts = projectConfig?.fonts?.map((item) => item.name) ?? [];
  const mappedFonts = projectFonts.length > 0 ? projectFonts : FONTS_OPTIONS;

  const activeThemeKey = projectConfig?.activeTheme;
  const activeTheme = activeThemeKey
    ? projectConfig?.themes?.[activeThemeKey]
    : undefined;
  const disclaimerComponentConfig =
    props.componentDetails.theme?.config?.disclaimerComponent ??
    activeTheme?.disclaimerComponent;
  const resolvedFont = disclaimerComponentConfig?.font ?? activeTheme?.font;
  const resolvedColor = disclaimerComponentConfig?.color ?? activeTheme?.color;
  const rteDefaultStyleValues = {
    ...(resolvedFont && { fontFamily: resolvedFont }),
    ...(resolvedColor && { color: resolvedColor }),
  };

  const hiddenToolbarItems: RichTextToolbarOptions[] = [
    RichTextToolbarOptions.STRIKE,
  ];

  const handleSubmit: SubmitHandler<IDisclaimerConfig> = (value) => {
    props.onSave(value);
  };

  const fontSize = {
    Indoor: ['15px', '18px', '31px'],
    default: ['13px', '16px', '23px'],
  };
  const hasBackgroundImage = (mop?: { background?: string }) =>
    typeof mop?.background === 'string' && mop.background.trim() !== '';

  let disclaimerRichTextStyles = {};
  if (props.componentDetails.theme?.config.disclaimerComponent) {
    const {
      color,
      fontSize: fontSizeEditor,
      lineHeight: lineHeightEditor,
    } = props.componentDetails.theme?.config.disclaimerComponent;

    disclaimerRichTextStyles = {
      textColor: color,
    };

    disclaimerRichTextStyles = { ...disclaimerRichTextStyles, fontSizeEditor };

    disclaimerRichTextStyles = {
      ...disclaimerRichTextStyles,
      lineHeightEditor,
    };
  }

  const items = [
    {
      label: 'Background',
      onLabelClick: onToggleBackgroundDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: props.hasTriedSaving
        ? !hasBackgroundImage(props.componentDetails.config)
        : false,
    },
    {
      label: 'Disclaimer',
      hasError: formState.errors?.text ? true : false,
      content: (
        <Grid container>
          <Grid item xs={12}>
            <Controller
              name="text"
              control={control}
              render={({ field: { ...field } }) => (
                <div>
                  <RichTextEditor
                    placeholder={options?.placeholder || ''}
                    value={field.value.replaceAll('&nbsp;', ' ')}
                    onChange={(value) =>
                      handleComponentConfigChange(
                        props,
                        'text',
                        convertSpacesToHTMLFormat(value as string),
                        field.onChange,
                      )
                    }
                    disabled={disabled}
                    theme={options?.richTextTheme}
                    fontFamily={mappedFonts}
                    fontSize={
                      props.componentDetails?.layoutType?.name === 'Indoor'
                        ? fontSize['Indoor']
                        : fontSize['default']
                    }
                    letterSpacing={['0px', '1px', '2px']}
                    hide={hiddenToolbarItems}
                    backgroundImage={getValues('background')}
                    {...disclaimerRichTextStyles}
                    pasteWithStyle={true}
                    defaultStyleValues={rteDefaultStyleValues}
                  />
                </div>
              )}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <FormWrapper
      form={props.form}
      formState={formState}
      formReset={reset}
      formGetValues={getValues}
      onSave={submitWrapper(handleSubmit)}>
      <SidebarAccordion items={items} />
      <Controller
        name="background"
        control={control}
        render={({ field, fieldState }) => (
          <ResourceSelectorSidebar
            label="Background"
            type={ResourceType.BACKGROUND}
            defaultAssetName={field.value}
            openResourceSelectorDrawer={openBackgroundDrawer}
            onToggleResourceSelectorDrawer={onToggleBackgroundDrawer}
            onChange={handleOnChange('background')}
            hasError={!!fieldState.error}
            error={fieldState.error?.message || false}
          />
        )}
      />
    </FormWrapper>
  );
};
